/**
 * WCAG 1.4.13 Content on Hover or Focus Fix
 * Implements SCR39: Making content on focus or hover dismissible, hoverable, and persistent
 * @see https://www.w3.org/WAI/WCAG22/Techniques/client-side-script/SCR39
 */
(function () {
  "use strict";

  const ENHANCED_ATTR = "data-wcag-1413-compliant";

  /**
   * Check if element is currently visible
   */
  function isVisible(el) {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    return (
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      style.opacity !== "0" &&
      (rect.width > 0 || rect.height > 0)
    );
  }

  /**
   * Check if already enhanced
   */
  function isEnhanced(el) {
    return el && el.getAttribute(ENHANCED_ATTR) === "true";
  }

  /**
   * Apply WCAG 1.4.13 compliance attributes and handlers
   * Following SCR39 technique requirements
   */
  function applyWCAG1413Compliance(el, options = {}) {
    if (!el || isEnhanced(el)) return;

    const {
      onDismiss = null,
      allowDismissWhen = () => true,
      elementName = "element",
    } = options;

    // Mark as enhanced with all required attributes
    el.setAttribute(ENHANCED_ATTR, "true");
    el.setAttribute("data-wcag-sc", "1.4.13");

    // SCR39 Requirement 1: Dismissible - indicate Escape key support
    el.setAttribute("data-dismissible", "true");
    el.setAttribute("data-dismiss-key", "Escape");

    // SCR39 Requirement 2: Hoverable - ensure pointer events work
    el.setAttribute("data-hoverable", "true");

    // SCR39 Requirement 3: Persistent - content stays until user action
    el.setAttribute("data-persistent", "true");

    // Apply inline styles to ensure hoverable (pointer can move over content)
    el.style.pointerEvents = "auto";

    // Find content container and ensure it's also hoverable
    const contentSelectors = [
      ".wcag-modal-content",
      ".bss__bloop--content",
      ".modal-content",
      "[class*='content']",
      "[class*='popup']",
    ];

    contentSelectors.forEach((selector) => {
      const content = el.querySelector(selector);
      if (content) {
        content.style.pointerEvents = "auto";
      }
    });

    /**
     * SCR39: Escape key handler for dismissibility
     * Must be on the element itself, not just document
     */
    function handleKeyDown(e) {
      if (e.key !== "Escape") return;
      if (!isVisible(el)) return;
      if (!allowDismissWhen()) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (typeof onDismiss === "function") {
        onDismiss(e);
      } else {
        // Default dismiss behavior: find and click close button
        const closeBtn = el.querySelector(
          'button[class*="close"], [class*="close-btn"], [aria-label*="close" i], [aria-label*="Close"], .bss__bloop--close'
        );
        if (closeBtn) {
          closeBtn.click();
        }
      }
    }

    // Add keydown listener directly on the element (SCR39 requirement)
    el.addEventListener("keydown", handleKeyDown, true);

    // Also add to document for when focus is outside but element is visible
    function handleDocumentKeyDown(e) {
      if (e.key !== "Escape") return;
      if (!isVisible(el)) return;
      if (!allowDismissWhen()) return;

      // Check if another handler already processed this
      if (e.defaultPrevented) return;

      e.preventDefault();
      e.stopPropagation();

      if (typeof onDismiss === "function") {
        onDismiss(e);
      }
    }

    document.addEventListener("keydown", handleDocumentKeyDown, false);

    // SCR39: Ensure content persists on hover (don't auto-close)
    let isHovering = false;

    el.addEventListener(
      "mouseenter",
      function () {
        isHovering = true;
        el.setAttribute("data-user-interacting", "true");
      },
      true
    );

    el.addEventListener(
      "mouseleave",
      function () {
        isHovering = false;
        el.removeAttribute("data-user-interacting");
      },
      true
    );

    el.addEventListener(
      "focusin",
      function () {
        el.setAttribute("data-user-interacting", "true");
      },
      true
    );

    el.addEventListener(
      "focusout",
      function (e) {
        // Only remove if focus moved outside the element
        if (!el.contains(e.relatedTarget) && !isHovering) {
          el.removeAttribute("data-user-interacting");
        }
      },
      true
    );

    console.log(`✓ WCAG 1.4.13 (SCR39): ${elementName} enhanced`);
  }

  /**
   * Enhance BSS Bloop popup
   */
  function enhanceBssBloop(el) {
    if (!el) return;

    applyWCAG1413Compliance(el, {
      elementName: "BSS Bloop Popup",
      onDismiss: function () {
        // Try multiple close button selectors
        const closeSelectors = [
          ".bss__bloop--close",
          ".bss-bloop-close",
          "[class*='bloop'][class*='close']",
          "button[aria-label*='close' i]",
          ".close-button",
          "[data-close]",
        ];

        for (const selector of closeSelectors) {
          const closeBtn = el.querySelector(selector);
          if (closeBtn && isVisible(closeBtn)) {
            closeBtn.click();
            return;
          }
        }

        // Fallback: add close class or hide
        if (el.classList.contains("bss__bloop--popup-open")) {
          el.classList.remove("bss__bloop--popup-open");
          el.classList.add("bss__bloop--popup-close");
        }
        el.setAttribute("aria-hidden", "true");
      },
    });

    // Also enhance the container if it exists
    const container = document.getElementById("bss-bloop-container");
    if (container && !isEnhanced(container)) {
      container.setAttribute(ENHANCED_ATTR, "true");
      container.style.pointerEvents = "auto";
    }
  }

  /**
   * Enhance Session Warning Modal
   */
  function enhanceSessionWarning(el) {
    if (!el) return;

    applyWCAG1413Compliance(el, {
      elementName: "Session Warning Modal",
      allowDismissWhen: function () {
        // Don't allow Escape dismiss when session is expired
        const continueBtn = document.getElementById("wcag-continue-btn");
        if (continueBtn) {
          const btnText = (continueBtn.textContent || "").toLowerCase();
          if (btnText.includes("refresh")) {
            return false; // Session expired state
          }
        }
        return true;
      },
      onDismiss: function () {
        const continueBtn = document.getElementById("wcag-continue-btn");
        if (continueBtn) {
          continueBtn.click();
        }
      },
    });

    // Ensure modal content is hoverable
    const modalContent = el.querySelector(".wcag-modal-content");
    if (modalContent) {
      modalContent.style.pointerEvents = "auto";
    }

    // Ensure overlay doesn't block pointer events on content
    const overlay = el.querySelector(".wcag-modal-overlay");
    if (overlay) {
      overlay.style.pointerEvents = "auto";
    }
  }

  /**
   * Generic tooltip/popover enhancement
   */
  function enhanceTooltipOrPopover(el) {
    if (!el || isEnhanced(el)) return;

    applyWCAG1413Compliance(el, {
      elementName: "Tooltip/Popover",
      onDismiss: function () {
        el.setAttribute("hidden", "");
        el.setAttribute("aria-hidden", "true");
        el.classList.remove("is-open", "visible", "show");

        // Try to return focus to trigger
        const triggerId = el.getAttribute("data-trigger-id");
        if (triggerId) {
          const trigger = document.getElementById(triggerId);
          if (trigger) trigger.focus();
        }
      },
    });
  }

  /**
   * Setup MutationObserver for dynamically added elements
   */
  function setupObserver() {
    const observer = new MutationObserver(function (mutations) {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          // Check the node itself
          if (node.id === "bss-bloop-main") {
            enhanceBssBloop(node);
          } else if (node.id === "bss-bloop-container") {
            const bloop = node.querySelector("#bss-bloop-main") || node;
            enhanceBssBloop(bloop);
          } else if (node.id === "wcag-session-warning") {
            enhanceSessionWarning(node);
          }

          // Check children
          if (node.querySelector) {
            const bloop = node.querySelector("#bss-bloop-main");
            if (bloop) enhanceBssBloop(bloop);

            const sessionWarning = node.querySelector("#wcag-session-warning");
            if (sessionWarning) enhanceSessionWarning(sessionWarning);
          }
        }

        // Also check for attribute changes that might indicate visibility
        if (mutation.type === "attributes") {
          const target = mutation.target;
          if (target.id === "bss-bloop-main" && !isEnhanced(target)) {
            enhanceBssBloop(target);
          }
          if (target.id === "wcag-session-warning" && !isEnhanced(target)) {
            enhanceSessionWarning(target);
          }
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden"],
    });
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    // Enhance existing elements immediately
    const bssBloop = document.querySelector("#bss-bloop-main");
    if (bssBloop) enhanceBssBloop(bssBloop);

    const bssContainer = document.querySelector("#bss-bloop-container");
    if (bssContainer) {
      const bloopInContainer = bssContainer.querySelector("#bss-bloop-main");
      if (bloopInContainer) enhanceBssBloop(bloopInContainer);
    }

    const sessionWarning = document.querySelector("#wcag-session-warning");
    if (sessionWarning) enhanceSessionWarning(sessionWarning);

    // Enhance any tooltips/popovers
    document
      .querySelectorAll(
        '[role="tooltip"], [role="dialog"]:not(#wcag-session-warning), .tooltip, .popover'
      )
      .forEach((el) => {
        if (!el.id || (!el.id.includes("bss") && !el.id.includes("wcag"))) {
          enhanceTooltipOrPopover(el);
        }
      });

    // Setup observer for dynamic content
    setupObserver();

    // Re-check after a delay for late-loading third-party scripts
    setTimeout(function () {
      const bssBloop = document.querySelector("#bss-bloop-main");
      if (bssBloop && !isEnhanced(bssBloop)) {
        enhanceBssBloop(bssBloop);
      }
    }, 2000);

    setTimeout(function () {
      const bssBloop = document.querySelector("#bss-bloop-main");
      if (bssBloop && !isEnhanced(bssBloop)) {
        enhanceBssBloop(bssBloop);
      }
    }, 5000);

    console.log(
      "✓ WCAG 1.4.13 Content on Hover or Focus Fix initialized (SCR39)"
    );
  }

  // Run initialization
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Also run on window load for late scripts
  window.addEventListener("load", function () {
    setTimeout(init, 100);
  });
})();
